import React, { useEffect, useState } from "react";
import {
  GetBrandsListETA,
  UpsertBrandETA,
} from "../../services/BrandManagement.Service";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Paper,
  Divider,
} from "@mui/material";

import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";

const BrandManagement = () => {
  const [brandList, setBrandList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [etaError, setEtaError] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [etaDays, setEtaDays] = useState("");

  const fetchBrandList = async () => {
    setLoading(true);
    setError(null);

    const start = Date.now();

    try {
      const response = await GetBrandsListETA();
      if (response && response.result) {
        setBrandList(response.result);
      } else {
        setError("Invalid response from server");
      }
    } catch (err) {
      setError(err.message || "Failed to fetch brand ETA list");
    } finally {
      const elapsed = Date.now() - start;
      const minDelay = 500; // Minimum loading time in ms

      if (elapsed < minDelay) {
        setTimeout(() => setLoading(false), minDelay - elapsed);
      } else {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchBrandList();
  }, []);

  const openEditDialog = (brand) => {
    setSelectedBrand(brand);
    setEtaDays(brand.etadays || brand.etaDays || "");
    setEditDialogOpen(true);
  };

  const closeEditDialog = () => {
    setEditDialogOpen(false);
    setSelectedBrand(null);
    setEtaDays("");
    setEtaError(""); // Clear ETA validation error
    setSaveError(""); // Clear save error when dialog is closed
  };

  const handleSave = async () => {
    if (!selectedBrand) return;
    if (etaDays < 0) {
      setEtaError("ETA Days cannot be negative");
      return;
    }

    const payload = {
      brandID: selectedBrand.brandid || selectedBrand.brandID,
      brandName: selectedBrand.brandname || selectedBrand.brandName,
      etadays: Number(etaDays),
    };

    setSaving(true);
    setSaveError(""); // clear previous error

    try {
      await UpsertBrandETA(payload);
      await fetchBrandList(); // ✅ Fetch updated data
      closeEditDialog(); // ✅ Close dialog on success
    } catch (err) {
      const message = err.message || "Error saving ETA days";
      setSaveError(message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box p={3}>
      <Paper elevation={1}>
        {loading ? (
          <CircularProgress
            sx={{
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
            }}
          />
        ) : error ? (
          <Typography color="error">{error}</Typography>
        ) : (
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: "#f2e9e6" }}>
                {/* <TableCell
                  sx={{
                    fontWeight: "bold",
                    fontSize: "15px",
                    borderRight: "3px solid #e0e0e0",
                    verticalAlign: "middle", // subtle grey line
                  }}
                  align="center"
                >
                  Brand ID
                </TableCell> */}
                <TableCell
                  sx={{
                    fontWeight: "bold",
                    fontSize: "15px",
                    borderRight: "3px solid #e0e0e0",
                    verticalAlign: "middle", // subtle grey line
                  }}
                  align="center"
                >
                  Brand Name
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: "bold",
                    fontSize: "15px",
                    borderRight: "3px solid #e0e0e0",
                    verticalAlign: "middle",
                  }}
                  align="center"
                >
                  Created On
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: "bold",
                    fontSize: "15px",
                    borderRight: "3px solid #e0e0e0",
                    verticalAlign: "middle",
                  }}
                  align="center"
                >
                  Modified On
                </TableCell>

                <TableCell
                  sx={{
                    fontWeight: "bold",
                    fontSize: "15px",
                    borderRight: "3px solid #e0e0e0",
                    verticalAlign: "middle", // subtle grey line
                  }}
                  align="center"
                >
                  ETA Days
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: "bold",
                    fontSize: "15px",
                    verticalAlign: "middle",
                  }}
                  align="center"
                >
                  Action
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {brandList.map((brand, index) => (
                <TableRow
                  key={brand.brandid || brand.brandID}
                  sx={{
                    backgroundColor: index % 2 === 0 ? "#ffffff" : "#faf7f6",
                  }}
                >
                  {/* <TableCell
                    align="center"
                    sx={{
                      fontSize: "14px",
                      borderRight: "1px solid #e0e0e0",
                      verticalAlign: "middle",
                    }}
                  >
                    {brand.brandid || brand.brandID}
                  </TableCell> */}
                  <TableCell
                    sx={{
                      fontSize: "14px",
                      borderRight: "1px solid #e0e0e0",
                    }}
                  >
                    {brand.brandname || brand.brandName}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontSize: "14px",
                      borderRight: "1px solid #e0e0e0",
                      verticalAlign: "middle",
                    }}
                  >
                    {brand.createdOn
                      ? new Date(brand.createdOn).toLocaleDateString("en-GB", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                        })
                      : "-"}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontSize: "14px",
                      borderRight: "1px solid #e0e0e0",
                      verticalAlign: "middle",
                    }}
                  >
                    {brand.modifiedOn
                      ? new Date(brand.modifiedOn).toLocaleDateString("en-GB", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                        })
                      : "-"}
                  </TableCell>

                  <TableCell
                    align="center"
                    sx={{
                      fontSize: "14px",
                      borderRight: "1px solid #e0e0e0",
                      verticalAlign: "middle",
                    }}
                  >
                    {brand.etaDays}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontSize: "14px",
                      borderRight: "1px solid #e0e0e0",
                      verticalAlign: "middle",
                    }}
                  >
                    <IconButton
                      onClick={() => openEditDialog(brand)}
                      size="small"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      <Dialog
        open={editDialogOpen}
        onClose={closeEditDialog}
        fullWidth
        maxWidth={false} // disables default maxWidth constraint
        sx={{
          "& .MuiPaper-root": { width: 400, maxWidth: "80vw" },
          justifyContent: "center",
        }} // custom width (300px or adjust)
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            Padding: "16px 24px",
          }}
        >
          <Typography variant="h4">
            Edit ETA for {selectedBrand?.brandname || selectedBrand?.brandName}
          </Typography>
          <IconButton
            aria-label="close"
            onClick={closeEditDialog}
            size="small"
            sx={{ ml: 2 }}
            disabled={saving} // disable while saving
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <TextField
            type="number"
            label="ETA Days"
            value={etaDays}
            onChange={(e) => {
              const value = Number(e.target.value);

              if (value > 366) {
                setEtaError("ETA Days cannot be more than 366");
                return; // stop further input
              } else if (value < 0) {
                setEtaError("ETA Days cannot be negative");
                return;
              } else {
                setEtaError("");
              }

              setEtaDays(e.target.value);
            }}
            fullWidth
            inputProps={{ min: 0, max: 366 }}
            error={Boolean(etaError)}
            helperText={etaError}
            sx={{
              mt: 1,
              "& .MuiFormLabel-root.Mui-error": {
                color: "inherit", // keep label default color even if error
              },
              "& .MuiFormHelperText-root.Mui-error": {
                color: "red", // helper text remains red
              },
            }}
          />
        </DialogContent>

        {/* Display save error if any */}
        {saveError && (
          <Typography color="error" align="center" sx={{ mt: 1 }}>
            {saveError}
          </Typography>
        )}

        {saveError && (
          <Typography color="error" align="center" sx={{ mt: 1 }}>
            {saveError}
          </Typography>
        )}

        <DialogActions sx={{ justifyContent: "center", marginBottom: "16px" }}>
          <Button
            onClick={handleSave}
            variant="contained"
            color="primary"
            disabled={saving || etaError}
            startIcon={saving && <CircularProgress size={20} color="inherit" />}
          >
            {saving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BrandManagement;
